import PullToRefresh from 'pulltorefreshjs';
import { UsePullToRefreshOptions } from './types';
import { useEffect, useRef } from 'react';

const LOCAL_SCROLLABLE_SELECTOR = [
  '[data-pull-to-refresh-disabled]',
  '[data-radix-scroll-area-viewport]',
  '[role="dialog"]'
].join(',');

const isElement = (value: EventTarget | null): value is HTMLElement => {
  return value instanceof HTMLElement;
};

const isTouchRefreshAvailable = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const isWindowScrolledToTop = (): boolean => {
  return window.scrollY <= 0 && document.documentElement.scrollTop <= 0 && document.body.scrollTop <= 0;
};

const isScrollableElement = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY;

  return element.scrollHeight > element.clientHeight && (overflowY === 'auto' || overflowY === 'scroll');
};

const isInsideLocalScrollableElement = (target: EventTarget | null): boolean => {
  if (!isElement(target)) {
    return false;
  }

  if (target.closest(LOCAL_SCROLLABLE_SELECTOR)) {
    return true;
  }

  let currentElement: HTMLElement | null = target;

  while (currentElement && currentElement !== document.body && currentElement !== document.documentElement) {
    if (isScrollableElement(currentElement)) {
      return true;
    }

    currentElement = currentElement.parentElement;
  }

  return false;
};

export const usePullToRefresh = (options: UsePullToRefreshOptions): void => {
  const {
    isEnabled = true,
    onRefresh
  } = options;

  const lastTouchTargetRef = useRef<EventTarget | null>(null);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!isEnabled || !isTouchRefreshAvailable()) {
      return;
    }

    const onTouchStart = (event: TouchEvent): void => {
      lastTouchTargetRef.current = event.target;
    };

    window.addEventListener('touchstart', onTouchStart, {
      capture: true,
      passive: true
    });

    const pullToRefresh = PullToRefresh.init({
      classPrefix: 'wl-ptr--',
      distMax: 72,
      distReload: 48,
      distThreshold: 64,
      iconArrow: '',
      iconRefreshing: '',
      instructionsPullToRefresh: 'Потяните вниз, чтобы проверить обновление',
      instructionsRefreshing: 'Проверяем обновление',
      instructionsReleaseToRefresh: 'Отпустите, чтобы проверить обновление',
      mainElement: 'body',
      onRefresh: () => onRefreshRef.current(),
      refreshTimeout: 300,
      shouldPullToRefresh: () => {
        return isWindowScrolledToTop() && !isInsideLocalScrollableElement(lastTouchTargetRef.current);
      },
      triggerElement: 'body'
    });

    return () => {
      window.removeEventListener('touchstart', onTouchStart, {
        capture: true
      });
      pullToRefresh.destroy();
    };
  }, [isEnabled]);
};